/**
 * SignupIntents.js — SuperAdmin: Abandoned Signup Tracking
 * Route suggestion: /superadmin/signup-intents
 *
 * FEATURE (this version): added a View modal — clicking a row (or a
 * dedicated View button) opens a read-only detail view showing every
 * captured field for that signup attempt, not just what fits in the
 * table columns (full name, both emails/phone formatted, company size,
 * industry, exact plan + billing cycle + promo code, and a precise
 * timestamp). Useful for Sales/Support before reaching out, so they
 * have full context on who they're contacting and why.
 *
 * Lists people who started signing up for PeoplesDock but didn't
 * complete — captured via fire-and-forget calls from TenantSignup.js
 * at four stages: typed their email/company name (step1_started),
 * reached Plan Selection (step2_reached), opened the payment popup
 * (payment_initiated), or closed it without paying (payment_abandoned).
 *
 * Lets Sales/Support follow up directly — "saw you started signing up,
 * anything we can help with?" — on real leads that would otherwise be
 * completely invisible, since nothing was previously recorded in the
 * database until a signup fully completed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, Button, Row, Col, Spinner, Alert, Modal } from 'react-bootstrap';
import {
    UserPlus, Mail, Phone, RefreshCw, Clock, CheckCircle, XCircle,
    Eye, Building2, Briefcase, Tag, Calendar,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const STEP_META = {
    step1_started:      { bg: 'secondary', label: 'Started Filling Form' },
    step2_reached:      { bg: 'info',      label: 'Reached Plan Selection' },
    payment_initiated:  { bg: 'warning',   label: 'Opened Payment' },
    payment_abandoned:  { bg: 'danger',    label: 'Abandoned at Payment' },
};

const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// ── View Detail Modal ────────────────────────────────────────────────────────
const ViewIntentModal = ({ show, onHide, row }) => {
    if (!row) return null;
    const meta = STEP_META[row.step_reached] || { bg: 'secondary', label: row.step_reached };
    const Field = ({ icon: Icon, label, value }) => (
        <Col xs={12} sm={6}>
            <div className="small text-muted fw-bold mb-1 d-flex align-items-center gap-1">
                {Icon && <Icon size={12}/>}{label}
            </div>
            <div className="fw-medium">{value || <span className="text-muted">—</span>}</div>
        </Col>
    );
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <Eye size={16} className="text-primary"/>
                    Signup Attempt Detail
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded-3"
                    style={{ background: row.is_completed ? '#f0fdf4' : '#fff7ed' }}>
                    <div>
                        <div className="fw-bold fs-5">
                            {row.first_name || row.last_name
                                ? `${row.first_name || ''} ${row.last_name || ''}`.trim()
                                : 'Unknown name'}
                        </div>
                        <div className="text-muted small">{row.email}</div>
                    </div>
                    <div className="text-end">
                        <Badge bg={meta.bg} className="mb-1 d-block">{meta.label}</Badge>
                        {row.is_completed ? (
                            <Badge bg="success" className="d-inline-flex align-items-center gap-1">
                                <CheckCircle size={11}/>Signed Up
                            </Badge>
                        ) : (
                            <Badge text="dark" className="d-inline-flex align-items-center gap-1" style={{ background: '#f1f5f9' }}>
                                <XCircle size={11}/>Did Not Complete
                            </Badge>
                        )}
                    </div>
                </div>
                <Row className="g-3">
                    <Col xs={12}>
                        <div className="fw-bold small text-muted text-uppercase mb-1" style={{ letterSpacing: 1 }}>
                            Contact
                        </div>
                    </Col>
                    <Field icon={Mail}     label="Email"   value={<a href={`mailto:${row.email}`}>{row.email}</a>}/>
                    <Field icon={Phone}    label="Phone"    value={row.phone}/>
                    <Col xs={12}><hr className="my-1"/></Col>
                    <Col xs={12}>
                        <div className="fw-bold small text-muted text-uppercase mb-1" style={{ letterSpacing: 1 }}>
                            Company
                        </div>
                    </Col>
                    <Field icon={Building2} label="Company Name" value={row.company_name}/>
                    <Field icon={Building2} label="Company Size" value={row.company_size ? `${row.company_size} employees` : null}/>
                    <Field icon={Briefcase} label="Industry"     value={row.industry}/>
                    <Col xs={12}><hr className="my-1"/></Col>
                    <Col xs={12}>
                        <div className="fw-bold small text-muted text-uppercase mb-1" style={{ letterSpacing: 1 }}>
                            Plan Interest
                        </div>
                    </Col>
                    <Field icon={Tag} label="Plan Selected" value={row.selected_plan_name}/>
                    <Field icon={Tag} label="Billing Cycle" value={row.billing_cycle}/>
                    <Field icon={Tag} label="Promo Code Tried" value={row.promo_code}/>
                    <Col xs={12}><hr className="my-1"/></Col>
                    <Col xs={12}>
                        <div className="fw-bold small text-muted text-uppercase mb-1" style={{ letterSpacing: 1 }}>
                            Timeline
                        </div>
                    </Col>
                    <Field icon={Calendar} label="First Seen"     value={fmtDate(row.created_at)}/>
                    <Field icon={Calendar} label="Last Activity"  value={fmtDate(row.updated_at)}/>
                    {row.is_completed && (
                        <Field icon={CheckCircle} label="Completed Signup As" value={row.completed_company_name}/>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide}>Close</Button>
                <Button variant="primary" href={`mailto:${row.email}`}>
                    <Mail size={14} className="me-1"/>Email Them
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const SignupIntents = () => {
    const [rows,    setRows]    = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState('');
    const [completedFilter, setCompletedFilter] = useState('false'); // default: show only those who DIDN'T complete
    const [viewRow, setViewRow] = useState(null); // FEATURE: view detail modal

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (filter) params.step_reached = filter;
        if (completedFilter !== '') params.completed = completedFilter;
        axiosInstance.get('/superadmin/signup-intents', { params })
            .then(r => {
                setRows(r.data.data?.rows || []);
                setSummary(r.data.data?.summary || null);
            })
            .catch(() => toast.error('Failed to load signup intents'))
            .finally(() => setLoading(false));
    }, [filter, completedFilter]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <UserPlus size={22} className="me-2 text-primary"/>
                        Abandoned Signups
                    </h4>
                    <p className="text-muted small mb-0">
                        People who started signing up but didn't complete — reach out and find out why.
                    </p>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={14} className="me-1"/>Refresh
                </Button>
            </div>

            {summary && (
                <Row className="g-3 mb-4">
                    {[
                        { label: 'Started Form',    value: summary.step1_started,     color: '#64748b' },
                        { label: 'Reached Plans',   value: summary.step2_reached,     color: '#0891b2' },
                        { label: 'Opened Payment',  value: summary.payment_initiated, color: '#d97706' },
                        { label: 'Abandoned Payment', value: summary.payment_abandoned, color: '#dc2626' },
                        { label: 'Completed',       value: summary.completed,         color: '#059669' },
                    ].map((s, i) => (
                        <Col key={i} xs={6} lg={2.4} md={4}>
                            <Card className="border-0 shadow-sm p-3 text-center h-100">
                                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value ?? 0}</div>
                                <div className="text-muted small">{s.label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <div className="d-flex gap-2 mb-3 flex-wrap">
                <span className="text-muted small fw-bold align-self-center me-1">Stage:</span>
                {['', ...Object.keys(STEP_META)].map(s => (
                    <Button key={s} size="sm"
                        variant={filter === s ? 'primary' : 'outline-secondary'}
                        onClick={() => setFilter(s)}>
                        {s ? STEP_META[s].label : 'All'}
                    </Button>
                ))}
            </div>
            <div className="d-flex gap-2 mb-4 flex-wrap">
                <span className="text-muted small fw-bold align-self-center me-1">Outcome:</span>
                {[
                    { v: 'false', label: 'Not Completed (needs follow-up)' },
                    { v: 'true',  label: 'Completed Anyway' },
                    { v: '',      label: 'All' },
                ].map(o => (
                    <Button key={o.v} size="sm"
                        variant={completedFilter === o.v ? 'dark' : 'outline-secondary'}
                        onClick={() => setCompletedFilter(o.v)}>
                        {o.label}
                    </Button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : rows.length === 0 ? (
                <Alert variant="info" className="text-center">
                    No signup intents found{filter ? ` for "${STEP_META[filter]?.label}"` : ''}.
                </Alert>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Name</th>
                                <th>Contact</th>
                                <th>Company</th>
                                <th>Plan Interested</th>
                                <th>Stage Reached</th>
                                <th>Last Activity</th>
                                <th>Outcome</th>
                                <th className="pe-4 text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const meta = STEP_META[r.step_reached] || { bg: 'secondary', label: r.step_reached };
                                return (
                                    <tr key={r.id} style={{ background: r.is_completed ? '#f0fdf4' : undefined, cursor: 'pointer' }}
                                        onClick={() => setViewRow(r)}>
                                        <td className="ps-4">
                                            <div className="fw-medium">
                                                {r.first_name || r.last_name
                                                    ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                                                    : <span className="text-muted">—</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <Mail size={12} className="text-muted"/>
                                                <a href={`mailto:${r.email}`} style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>{r.email}</a>
                                            </div>
                                            {r.phone && (
                                                <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: 11 }}>
                                                    <Phone size={11}/>{r.phone}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div>{r.company_name || <span className="text-muted">—</span>}</div>
                                            {r.company_size && (
                                                <div className="text-muted" style={{ fontSize: 10 }}>{r.company_size} employees</div>
                                            )}
                                        </td>
                                        <td>
                                            {r.selected_plan_name
                                                ? <Badge bg="light" text="dark">{r.selected_plan_name}</Badge>
                                                : <span className="text-muted">—</span>}
                                            {r.promo_code && (
                                                <div className="text-muted" style={{ fontSize: 10 }}>Promo: {r.promo_code}</div>
                                            )}
                                        </td>
                                        <td><Badge bg={meta.bg}>{meta.label}</Badge></td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: 11 }}>
                                                <Clock size={11}/>{fmtDate(r.updated_at)}
                                            </div>
                                        </td>
                                        <td>
                                            {r.is_completed ? (
                                                <Badge bg="success" className="d-inline-flex align-items-center gap-1">
                                                    <CheckCircle size={11}/>Signed Up
                                                </Badge>
                                            ) : (
                                                <Badge text="dark" className="d-inline-flex align-items-center gap-1" style={{ background: '#f1f5f9' }}>
                                                    <XCircle size={11}/>Did Not Complete
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="pe-4 text-end">
                                            <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                                                onClick={e => { e.stopPropagation(); setViewRow(r); }}>
                                                <Eye size={12} className="me-1"/>View
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}

            <ViewIntentModal show={!!viewRow} onHide={() => setViewRow(null)} row={viewRow}/>
        </div>
    );
};
export default SignupIntents;
